import { supabase } from "@/supabase";
import { subscriptionManager } from "@/realtime/SubscriptionManager";
import { Project } from "@/helpers/Interfaces";
import { Line, ImageInfo, Rule, Rules, ImageData } from "@/helpers/Interfaces";
import type { RealtimeChannel } from "@supabase/supabase-js";

type AnnotationRow = {
  id: number;
  project_id: number;
  image_id: number;
  data: any;
  is_hidden: boolean;
};

export default class ProjectService {
  static _presenceChannels: Map<number, RealtimeChannel> = new Map();
  static _presenceCallbacks: Map<number, Set<(map: Record<number, Array<{ key: string; email: string }>>) => void>> = new Map();
  static _presenceRefCount: Map<number, number> = new Map();
  static _meId: string | null = null;
  static _meEmail: string | null = null;
  static _annoGen: Map<string, number> = new Map();

  // Gets all projets from projects table
  static async getAllProjects(): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
    
    if (error) {
      console.error("Error getting projects:", error)
      throw error
    }

    // If there are no projects returns []
    return data as Project[]
  }

  static async getProjectIdByImage(imageId: number): Promise<number> {
    const { data, error } = await supabase
      .from('project_images')
      .select('project_id')
      .eq('id', imageId)
      .single();
    if (error) throw error;
    return data.project_id as number;
  }

  // Adds the newly created project to projects table
  static async createProject(project: Project, mainImage?: File): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        project_name: project.project_name,
        project_description: project.project_description,
        nr_images: project.nr_images ?? 0,
        project_image: project.project_image ?? null
      })
      .select('*')
      .single()

    if (error) {
      throw error
    }
    return data as Project
  }

  static async updateProject(projectId: number, updates: Partial<Project>): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId);

    if (error) {
      throw error;
    }
  }

  // Gets the project passing the Project ID
  static async getProjectById(projectId: number): Promise<Project | null> {
    const { data, error } = await supabase
      .from("projects")
      .select("*")
      .eq("id", projectId)
      .maybeSingle();

    if (error) {
      console.error("Error getting projet:", error);
      return null;
    }
    
    return data as Project;
  }

  // Gets the project passing the Project Name
  static async getProjectByName(name: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('project_name', name)
      .maybeSingle()
  
    if (error) {
      if (error.code === 'PGRST116') { // If no project found
        return null
      }
      throw error // Otherwise it returns an error
    }
    return data as Project
  }

  // Get all images from selected project (Prooject ID)
  static async getImagesFromProject(projectId: number): Promise<ImageInfo[]> {
    const { data, error } = await supabase
      .from('project_images')
      .select('id, project_id, file_name, width, height, has_lines,is_validated, validated_by, validated_at, is_invalidated, invalidated_by, invalidated_at, invalidate_notes, review_lock_by, review_lock_until, is_non_trackable')
      .eq('project_id', projectId)
      .order("file_name", { ascending: true });
  
    if (error) {
      console.error("Erro ao obter imagens:", error);
      throw error;
    }
  
    return data as ImageInfo[];
  }

  static async setImageNonTrackable(imageId: number, value: boolean): Promise<void> {
    const { error } = await supabase
      .from('project_images')
      .update({ is_non_trackable: value })
      .eq('id', imageId);

    if (error) throw error;
  }

  static async getProjectImageData(projectId: number): Promise<ImageData[]> {
    try {
      const { data: annotationImages, error: annotationImagesError } = await supabase
        .from('annotations')
        .select('image_id')
        .eq('project_id', projectId);
      
      if (annotationImagesError) throw annotationImagesError;
      if (!annotationImages || annotationImages.length === 0) return [];
      
      const imageIds = [...new Set(annotationImages.map(ann => ann.image_id))];
      
      const { data: images, error: imageError } = await supabase
        .from('project_images')
        .select('id, file_name, width, height, has_lines')
        .in('id', imageIds);
      
      if (imageError) throw imageError;
      if (!images) return [];
      
      const { data: annotations, error: annotationError } = await supabase
        .from('annotations')
        .select('image_id, data')
        .in('image_id', imageIds);
      
      if (annotationError) throw annotationError;
      
      const annotationsMap = new Map<number, Line[]>();
      annotations?.forEach(annotation => {
        const lineData: Line = annotation.data;
        if (!annotationsMap.has(annotation.image_id)) {
          annotationsMap.set(annotation.image_id, []);
        }
        annotationsMap.get(annotation.image_id)!.push(lineData);
      });
      
      const imageDataArray: ImageData[] = images.map(img => {
        const lines: Line[] = annotationsMap.get(img.id) || [];
        return {
          imageUrl: `${supabase.storage.from('project-images').getPublicUrl(`${projectId}/${img.file_name}`).data.publicUrl}`,
          imageName: img.file_name,
          imageWidth: img.width,
          imageHeight: img.height,
          lines: {
            canvasWidth: img.width,
            canvasHeight: img.height,
            lines: lines
          }
        };
      });
      
      return imageDataArray;
    } catch (error) {
      console.error('Erro ao buscar imagens do projeto:', error);
      return [];
    }
  }

  static async getValidatedProjectImageData(projectId: number): Promise<ImageData[]> {
    try {
      const { data: images, error: imgError } = await supabase
        .from("project_images")
        .select("id, file_name, width, height, has_lines, is_validated, is_invalidated, is_non_trackable")
        .eq("project_id", projectId)
        .eq("is_validated", true)
        .or("is_invalidated.is.null,is_invalidated.eq.false")
        .order("file_name", { ascending: true });

      if (imgError) throw imgError;

      const list = (images ?? []) as Array<{
        id: number;
        file_name: string;
        width: number;
        height: number;
        is_non_trackable?: boolean | null;
      }>;

      if (!list.length) return [];

      const byId = new Map<number, typeof list[number]>();
      list.forEach(i => byId.set(i.id, i));

      // buscar annotations só para as imagens validadas (evita puxar o projecto todo)
      const ids = list.map(i => i.id);

      const { data: anns, error: annError } = await supabase
        .from("annotations")
        .select("image_id, data")
        .eq("project_id", projectId);

      if (annError) throw annError;

      const grouped = new Map<number, Line[]>();
      (anns ?? []).forEach(row => {
        const imgId = row.image_id as number;
        if (!byId.has(imgId)) return;
        const line = row.data as Line;
        if (!grouped.has(imgId)) grouped.set(imgId, []);
        grouped.get(imgId)!.push(line);
      });

      // build ImageData para TODAS as imagens validadas
      const out: ImageData[] = [];
      for (const meta of list) {
        const { data } = supabase.storage
          .from("project-images")
          .getPublicUrl(`${projectId}/${meta.file_name}`);

        const lines = grouped.get(meta.id) ?? [];

        out.push({
          imageUrl: data.publicUrl,
          imageName: meta.file_name,
          imageWidth: meta.width,
          imageHeight: meta.height,
          // adiciona isto para o teu export conseguir ver
          is_non_trackable: !!meta.is_non_trackable,
          lines: {
            canvasWidth: meta.width,
            canvasHeight: meta.height,
            lines
          }
        } as any);
      }

      return out;
    } catch (error) {
      console.error("Erro em getValidatedProjectImageData:", error);
      return [];
    }
  }

  static async uploadProjectImage(image: File): Promise<void> {
    const fileName = image.name;
    const filePath = `projects-images/${fileName}`;
  
    const { error: uploadError } = await supabase.storage
      .from("project-images")
      .upload(filePath, image, { upsert: true });
  
    if (uploadError) {
      console.error("Error uploading main project image:", uploadError);
      throw uploadError;
    }
  }

  static async uploadImagesToSupabaseStorage(projectId: number, images: File[], updateProgress?: (current: number, total: number) => void, concurrencyLimit: number = 3): Promise<Array<{ file_name: string; width: number; height: number }>> {

    const results: Array<{ file_name: string; width: number; height: number }> = [];
    let uploadedCount = 0;
    const totalImages = images.length;

    updateProgress?.(uploadedCount, totalImages);

    const queue = [...images];

    async function worker() {
      while (queue.length > 0) {
        const image = queue.shift();
        if (!image) continue;

        try {
          const dimensions = await getImageDimensions(image);

          const { error } = await supabase.storage
            .from("project-images")
            .upload(`${projectId}/${image.name}`, image);

          if (error) {
            console.error("Upload error:", image.name, error);
            continue;
          }

          results.push({
            file_name: image.name,
            width: dimensions.width,
            height: dimensions.height
          });

          uploadedCount++;
          updateProgress?.(uploadedCount, totalImages);

        } catch (err) {
          console.error("Upload failed for", image.name, err);
        }
      }
    }

    const workers = Array.from({ length: concurrencyLimit }, () => worker());
    await Promise.all(workers);

    return results;
  }

  // Stores image data in project_images table
  static async insertProjectImagesMetadata(projectId: number, imagesMetadata: Array<{ file_name: string; width: number; height: number }>) {
    const payload = imagesMetadata.map((img) => ({
      project_id: projectId,
      file_name: img.file_name,
      width: img.width,
      height: img.height,
      has_lines:0
    }))

    const { error } = await supabase
      .from('project_images')
      .insert(payload)

    if (error) {
      console.error('Error inserting image data', error)
      throw error
    }
  }

  // Update nr_images on table projects
  static async updateProjectImageCount(projectId: number, nrImages: number) {
    const { error } = await supabase
      .from('projects')
      .update({ nr_images: nrImages })
      .eq('id', projectId)

    if (error) {
      console.error('Error updating nr_images', error)
      throw error
    }
  }

  static async updateProjectImageCountFromTable(projectId: number) {
    // Counts the number of images in the project
    const { count, error: countError } = await supabase
      .from('project_images')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
  
    if (countError) {
      console.error('Error counting images', countError)
      throw countError
    }
  
    // Updates nr_images in given project
    const { error: updateError } = await supabase
      .from('projects')
      .update({ nr_images: count })
      .eq('id', projectId)
  
    if (updateError) {
      console.error('Error updating nr_images', updateError)
      throw updateError
    }
  }
  

  // Deletes the project all images associated with the project
  // - Removes data from project_images table
  // - Removes images from project-images bucket
  // - Removes project from projects table
  static async deleteProject(project: Project) {
    // Gets image data from project_images
    const { data: imagesMetadata, error: imgErr } = await supabase
      .from('project_images')
      .select('*')
      .eq('project_id', project.id)

    if (imgErr) {
      console.error("Erro ao obter imagens do projeto:", imgErr)
      throw imgErr
    }

    // Gets all images path 
    if (imagesMetadata && imagesMetadata.length > 0) {
      const paths = imagesMetadata.map(
        (img) => `${project.id}/${img.file_name}`
      )

      // Removes all images from project-images bucket
      const { error: delStorageErr } = await supabase
        .storage
        .from('project-images')
        .remove(paths)

      if (delStorageErr) {
        console.error("Error removing image from bucket:", delStorageErr)
        throw delStorageErr
      }
    }

    // Removes image data from project_images table
    const { error: delImgErr } = await supabase
      .from('project_images')
      .delete()
      .eq('project_id', project.id)

    if (delImgErr) {
      console.error("Error removing image data:", delImgErr)
      throw delImgErr
    }

    // Removes the project from projects table
    const { error: delProjErr } = await supabase
      .from('projects')
      .delete()
      .eq('id', project.id)

    if (delProjErr) {
      console.error("Error removing project:", delProjErr)
      throw delProjErr
    }
  }

  // Get image path from given Project ID and image name
  static getImageUrl(project_id: number, img_filename: string): string {
    const { data } = supabase.storage
      .from('project-images')
      .getPublicUrl(`${project_id}/${img_filename}`);
    return data?.publicUrl || '';
  }

static getThumbUrl(project_id: number, img_filename: string, w = 140, h = 78, q = 75): string {
  const { data } = supabase
    .storage
    .from('project-images')
    .getPublicUrl(`${project_id}/${img_filename}`, {
      transform: { width: w, height: h, resize: 'cover', quality: q },
    });
  return data?.publicUrl || '';
}

  // Fallback: Signed URL
  static async getSignedThumbUrl(project_id: number, img_filename: string, expires = 60, w = 140, h = 140, q = 75): Promise<string> {
    const { data, error } = await supabase
      .storage
      .from('project-images')
      .createSignedUrl(`${project_id}/${img_filename}`, expires, {
        transform: { width: w, height: h, resize: 'cover', quality: q },
      });
    if (error) throw error;
    return data.signedUrl;
  }

  // Get image annotations
  static async getAnnotationsForImage(imageId: number): Promise<Line[]> {
    const { data, error } = await supabase
        .from("annotations")
        .select("id, data, is_hidden")
        .eq("image_id", imageId);

    if (error) {
        console.error("Error getting annotations:", error);
        return [];
    }

    return (data ?? []).map(row => {
      const payload = row.data ?? {};
      return { ...payload, id: row.id, is_hidden: row.is_hidden ?? false } as Line;
    });
  }

  static async getProjectImageUrl(projectId: number): Promise<string> {
    const selectedProject = await this.getProjectById(projectId);

    if (selectedProject?.project_image) {
      const { data } = supabase.storage
        .from("project-images")
        .getPublicUrl(`projects-images/${selectedProject.project_image}`);

        return data?.publicUrl || "";
    }
      
    return "";
  }

  static async subscribeToPresence(projectId: number, cb: (map: Record<number, Array<{ key: string; email: string }>>) => void): Promise<void> {
    // Registers callback
    if (!this._presenceCallbacks.has(projectId)) {
      this._presenceCallbacks.set(projectId, new Set());
    }
    this._presenceCallbacks.get(projectId)!.add(cb);

    // Adds to refcount
    const n = (this._presenceRefCount.get(projectId) ?? 0) + 1;
    this._presenceRefCount.set(projectId, n);

    // se já existe canal, apenas faz rebuild para este cb receber o estado atual
    if (this._presenceChannels.has(projectId)) {
      this.rebuildPresenceMap(projectId);
      return;
    }

    // criar canal de presença
    //const { data: auth } = await supabase.auth.getUser();
    //const user = auth?.user || null;
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user ?? null;
    this._meId = user?.id ?? null;
    this._meEmail = (user?.email as string) ?? null;

    const ch = supabase.channel(`presence:project:${projectId}`, {
      config: { presence: { key: this._meId || `anon:${Math.random().toString(36).slice(2)}` } }
    });

    ch.on('presence', { event: 'sync' }, () => {
      this.rebuildPresenceMap(projectId);
    });

    await ch.subscribe(async (status) => {
      if (status !== 'SUBSCRIBED') return;
      await ch.track({ email: this._meEmail ?? '', image_id: null });
    });

    this._presenceChannels.set(projectId, ch);
    // Notify all registered callbacks
    this.rebuildPresenceMap(projectId);
  }

  static async unsubscribeFromPresence(projectId: number): Promise<void> {
    // decrementa refcount
    const curr = this._presenceRefCount.get(projectId) ?? 0;
    const next = Math.max(0, curr - 1);
    this._presenceRefCount.set(projectId, next);

    // se ainda há subscritores, mantém canal ligado
    if (next > 0) return;

    // último a sair: encerra canal e limpa tudo
    const ch = this._presenceChannels.get(projectId);
    if (ch) {
      try { await ch.unsubscribe(); } catch {}
    }
    this._presenceChannels.delete(projectId);
    this._presenceCallbacks.delete(projectId);
    this._presenceRefCount.delete(projectId);
  }

  static async setHasLinesFlag(imageId: number, value: boolean): Promise<void> {
    const { data, error } = await supabase
      .from('project_images')
      .update({ has_lines: value })
      .eq('id', imageId);
    if (error) throw error;
  }

  static async recomputeHasLines(imageId: number): Promise<void> {
    const projectId = await this.getProjectIdByImage(imageId);
    const { count, error } = await supabase
      .from('annotations')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('image_id', imageId);
    if (error) throw error;
    await this.setHasLinesFlag(imageId, (count ?? 0) > 0);
  }

  // Insert new image annotation
  static async insertAnnotation(imageId: number, payload: Omit<Line,'id'>): Promise<Line> {
    const projectId = await this.getProjectIdByImage(imageId);

    const { id: _drop, is_hidden, ...clean } = payload as Line;

    const { data, error } = await supabase
      .from('annotations')
      .insert({ project_id: projectId, image_id: imageId, data: clean, is_hidden: !!is_hidden })
      .select('*')
      .single();
    if (error) throw error;

    subscriptionManager.markLocalEcho(subscriptionManager.topicAnnotations(imageId), data.id);

    await this.setHasLinesFlag(imageId, true);

    const line: Line = { ...(data.data ?? {}), id: data.id };
    return line;
  }

  static async updateAnnotation(imageId: number, lines: Line[]): Promise<void> {
    const projectId = await this.getProjectIdByImage(imageId);
    if (!lines.length) return;

    // Strip "id" do JSON e atualiza por row-id
    const cleaned = lines.map((l) => {
      const { id, is_hidden, ...clean } = l as Line;
      return { id, clean, is_hidden: !!is_hidden };
    });

    // Atualiza cada row (só o JSON "data") e marca local echo
    const topic = subscriptionManager.topicAnnotations(imageId);

    const results = await Promise.all(
      cleaned.map(async ({ id, clean, is_hidden }) => {
        const { data, error } = await supabase
          .from('annotations')
          .update({ data: clean, is_hidden: !!is_hidden })
          .eq('project_id', projectId)
          .eq('image_id', imageId)
          .eq('id', id)
          .select('id')
          .single();

        if (error) throw error;
        return data?.id as number;
      })
    );

    results.forEach((rowId) => {
      if (rowId != null) subscriptionManager.markLocalEcho(topic, rowId);
    });

    await this.recomputeHasLines(imageId);
  }

  static async setAnnotationHidden(imageId: number, lineId: number, hidden: boolean): Promise<void> {
    const projectId = await this.getProjectIdByImage(imageId);

    const { error } = await supabase
      .from('annotations')
      .update({ is_hidden: hidden })
      .eq('project_id', projectId)
      .eq('image_id', imageId)
      .eq('id', lineId);

    if (error) throw error;
  }

  // Deletes image annotation
  static async deleteAnnotation(imageId: number, lineId: number): Promise<void> {
    // 1) tenta apagar por row.id
    let { data, error } = await supabase
      .from('annotations')
      .delete()
      .eq('image_id', imageId)
      .eq('id', lineId)
      .select('id'); // para perceber se apagou algo

    if (error) throw error;

    // 2) fallback: alguns registos antigos tinham "data.id"
    if (!data || data.length === 0) {
      const res2 = await supabase
        .from('annotations')
        .delete()
        .eq('image_id', imageId)
        .filter('data->>id', 'eq', String(lineId))
        .select('id');

      if (res2.error) throw res2.error;
    }

    // IMPORTANTE: NÃO faças markLocalEcho aqui.
    // Deixa o evento realtime "annotations:delete" chegar ao ImageCanvas e ao Labels.
    await this.recomputeHasLines(imageId);
  }

  static async deleteImages(projectId: number, imageIds: number[]) {
      const { data: images, error: imgError } = await supabase
          .from("project_images")
          .select("file_name")
          .in("id", imageIds);
      
      if (imgError) throw imgError;
      
      const filePaths = images.map(img => `${projectId}/${img.file_name}`);
      
      await supabase.storage.from("project-images").remove(filePaths);
      await supabase.from("project_images").delete().in("id", imageIds);
  }  

  static async deleteAllAnnotations(imageId: number) {
      try {
          const { data: projectData, error: projectError } = await supabase
              .from("project_images")
              .select("project_id")
              .eq("id", imageId)
              .maybeSingle();

          if (projectError) {
              console.error("Error getting `project_id`:", projectError);
              throw projectError;
          }

          if (!projectData?.project_id) {
              console.warn("No `project_id` found for image:", imageId);
              return;
          }

          const projectId = projectData.project_id;

          const { error: deleteError } = await supabase
              .from("annotations")
              .delete()
              .eq("project_id", projectId)
              .eq("image_id", imageId);

          if (deleteError) {
              console.error("Error deleting annotations:", deleteError);
              throw deleteError;
          }

          const { error: updateHasLinesError } = await supabase
              .from("project_images")
              .update({ has_lines: false })
              .eq("project_id", projectId)
              .eq("id", imageId);

          if (updateHasLinesError) {
              console.error("Error updating has_lines:", updateHasLinesError);
              throw updateHasLinesError;
          }

          console.log(`All annotations deleted for image ID ${imageId}`);
      } catch (error) {
          console.error("Error deleting all annotations:", error);
          throw error;
      }
  }

  // Checks if image has annotations
  static async hasAnnotations(projectId: number, imageId: number): Promise<boolean> {
      const { data, error } = await supabase
          .from("annotations")
          .select("id")
          .eq("project_id", projectId)
          .eq("image_id", imageId);

      if (error) {
          console.error("Error checking image annotations:", error);
          return false;
      }

      return (data && data.length > 0) ? true : false;
  }

  static async rebuildAndGetRules(projectId: number): Promise<Rules> {
      const neutralRules = {
          exportWidth: 480,
          exportHeight: 270,
          rules: [],
          exportImages: true,
          lineSize: 3,
          backgroundColor: "#000000"
      };

      // Get unique labels from annotations
      const { data: annotations, error: annotationsError } = await supabase
          .from("annotations")
          .select("data->>label") // Get label as text
          .eq("project_id", projectId);

      if (annotationsError) {
          console.error("Erro ao buscar anotações:", annotationsError);
          return neutralRules;
      }

      // Extract valid unique labels
      const annotationLabels = [...new Set(
          annotations?.map(a => a.label).filter((l): l is string => !!l)
      )];

      // Gets existing rules
      const { data: existingRules, error: rulesError } = await supabase
          .from('rules')
          .select('*')
          .eq("project_id", projectId);

      if (rulesError) {
          console.error("Erro ao buscar regras:", rulesError);
          return neutralRules;
      }

      const rulesMap = new Map<string, Rule>();
      
      // Add existing rules
      existingRules?.forEach(rule => rulesMap.set(rule.label, rule));
      
      // Add missing labels with default color
      annotationLabels.forEach(label => {
          if (!rulesMap.has(label)) {
              rulesMap.set(label, {
                  id: -1,
                  label,
                  color: "#ffffff"
              });
          }
      });

      const naturalCompare = (a: string, b: string) => 
        a.localeCompare(b, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
        });
    
      const mergedRules = Array.from(rulesMap.values())
        .sort((a, b) => naturalCompare(a.label, b.label));

      // Get configurations
      const { data: settings, error: settingsError } = await supabase
          .from('settings')
          .select('*')
          .eq("project_id", projectId)
          .maybeSingle();

      return {
          rules: mergedRules,
          exportWidth: settings?.export_width ?? neutralRules.exportWidth,
          exportHeight: settings?.export_height ?? neutralRules.exportHeight,
          exportImages: settings?.export_images ?? neutralRules.exportImages,
          lineSize: settings?.line_size ?? neutralRules.lineSize,
          backgroundColor: settings?.background_color ?? neutralRules.backgroundColor
      };
  }

  static async saveRules(projectId: number, rules: Rules): Promise<void> {
    const neutralRules = { exportWidth:480, exportHeight: 270, rules: [], exportImages: true, lineSize:3, backgroundColor: "#000000" };

    const { error: deleteSettingsError } = await supabase
      .from("settings")
      .delete()
      .eq("project_id", projectId);
    
    if (deleteSettingsError) {
          console.error("Error deleting rules:", deleteSettingsError);
          return;
    }

    const { error: deleteError } = await supabase
      .from("rules")
      .delete()
      .eq("project_id", projectId);
    
    if (deleteError) {
          console.error("Error deleting rules:", deleteError);
          return;
    }

    const { error: insertSettingsError } = await supabase
      .from("settings")
      .insert({ 
        export_width: rules.exportWidth,
        export_height: rules.exportHeight,
        export_images: rules.exportImages,
        line_size: rules.lineSize,
        background_color: rules.backgroundColor,
        project_id: projectId });

    if (insertSettingsError) {
        console.error("Error inserting settings:", insertSettingsError);
    }        

    if (!rules) {
      rules = neutralRules;
    }

    if (rules.rules.length > 0) {
      const rulesToInsert = rules.rules.map(rule => ({
        label: rule.label,
        color: rule.color,
        project_id: projectId
      }));

      const { error: insertError } = await supabase
        .from("rules")
        .insert(rulesToInsert)
        .select();

      if (insertError) {
          console.error("Error inserting rules:", insertError);
          throw insertError; 
      }
    }
  }

  static async setProjectUsers(projectId: number, userIds: string[]) {
    // Remove old users
    const { error: deleteError } = await supabase
      .from("project_users")
      .delete()
      .eq("project_id", projectId);

    if (deleteError) throw deleteError;

    // Add users
    if (userIds.length > 0) {
      const payload = userIds.map(userId => ({
        project_id: projectId,
        user_id: userId,
      }));

      const { error: insertError } = await supabase
        .from("project_users")
        .insert(payload);

      if (insertError) throw insertError;
    }
  }

  static async getAllUsers(): Promise<{ user_id: string; username: string; email: string }[]> {
    const { data, error } = await supabase
      .from("profiles")
      .select("user_id, username, email")
      .eq("isapproved", true);

    if (error) throw error;
    return data || [];
  }

  static async getUserIdsForProject(projectId: number): Promise<string[]> {
    const { data, error } = await supabase
      .from("project_users")
      .select("user_id")
      .eq("project_id", projectId);

    if (error) throw error;
    return data?.map(entry => entry.user_id) || [];
  }

  static async getUserNameById(userId: string | null | undefined): Promise<string> {
    if (!userId) return "Reviewer";
    const { data, error } = await supabase
      .from("profiles")
      .select("username")
      .eq("user_id", userId)
      .single();

    if (error) throw error;
    return data?.username || "Reviewer";
  }

  static async getProjectsForCurrentUser(): Promise<Project[]> {
    //const { data: authData, error: authError } = await supabase.auth.getUser();
    const { data: { session }, error: authError } = await supabase.auth.getSession();
    const user = session?.user ?? null;    
    if (authError || !user) {
      throw new Error("Not authenticated");
    }

    const userId = user?.id;

    // Check if user is admin
    const { data: profile, error: profileError } = await supabase
      .from("profiles")
      .select("isadmin")
      .eq("user_id", userId)
      .single();

    if (profileError) throw profileError;

    if (profile?.isadmin) {
      // Admin is able to access everything
      const { data, error } = await supabase.from("projects").select("*");
      if (error) throw error;
      return data as Project[];
    } else {
      // User just sees projects enabled for him
      const { data: links, error: linksError } = await supabase
        .from("project_users")
        .select("project_id")
        .eq("user_id", userId);

      if (linksError) throw linksError;

      const projectIds = links.map(link => link.project_id);

      const { data: projects, error: projectsError } = await supabase
        .from("projects")
        .select("*")
        .in("id", projectIds);

      if (projectsError) throw projectsError;
      return projects as Project[];
    }
  }
  
  static async isCurrentUserAdmin(): Promise<boolean> {
    //const { data: { user }, error: authError } = await supabase.auth.getUser();
    const { data: { session }, error: authError } = await supabase.auth.getSession();
    const user = session?.user ?? null;

    if (authError || !user) {
      console.error("Failed to get current user:", authError);
      return false;
    }

    const { data, error } = await supabase
      .from("profiles")
      .select("isadmin")
      .eq("user_id", user.id)
      .single();

    if (error || !data) {
      console.error("Failed to fetch user profile:", error);
      return false;
    }

    return data.isadmin === true;
  }

  static async isCurrentUserReviewer(projectId?: number): Promise<boolean> {
    if (!projectId) {
      return false;
    }
    const { data, error } = await supabase.rpc('is_reviewer', { p_project_id: projectId });
    console.log(data);
    if (error) {
      console.warn('is_reviewer RPC failed:', error);
      return false;
    }
    return !!data;
  }

  static async getProjectImageCount(projectId: number): Promise<number> {
    const { data, error } = await supabase
      .from('projects')
      .select('nr_images')
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return data?.nr_images ?? 0;
  }

  static async trackPresenceSelection(projectId: number, imageId: number | null): Promise<void> {
    const ch = this._presenceChannels.get(projectId);
    if (!ch) return;
    await ch.track({
      email: this._meEmail ?? '',
      image_id: imageId
    });
  }
  
  // Rebuild presence { image_id: [ {key,email} ] } and notifies
  private static rebuildPresenceMap(projectId: number) {
    const ch = this._presenceChannels.get(projectId);
    if (!ch) return;

    const raw = (ch as any).presenceState?.() as Record<string, Array<any>> || {};
    const me = this._meId;
    const map: Record<number, Array<{ key: string; email: string }>> = {};

    for (const [key, metas] of Object.entries(raw)) {
      if (me && key === me) continue; // don't show local user
      for (const meta of metas) {
        const imageId: number | null = meta?.image_id ?? null;
        const email = meta?.email ?? '';

        const bucket = (imageId == null) ? 0 : imageId;
        if (!map[bucket]) map[bucket] = [];
        if (!map[bucket].some(u => u.key === key)) {
          map[bucket].push({ key, email });
        }
      }
    }

    Object.keys(map).forEach(id => {
      map[+id].sort((a, b) => (a.email || '').localeCompare(b.email || ''));
    });

    const cbs = this._presenceCallbacks.get(projectId);
    if (cbs && cbs.size) {
      cbs.forEach(fn => {
        try { fn(map); } catch {}
      });
    }
  }

  static async claimNextImageForReview(projectId: number, excludeIds: number[] = []): Promise<ImageInfo | null> {
    const { data, error } = await supabase.rpc('claim_next_image_for_review', { p_project_id: projectId, p_exclude_ids: excludeIds });
    if (error) throw error;
    return data as ImageInfo | null;
  }

  static async heartbeatReviewLock(imageId: number): Promise<void> {
    const { error } = await supabase.rpc('heartbeat_review_lock', { p_image_id: imageId });
    if (error) throw error;
  }

  static async releaseReviewLock(imageId: number): Promise<void> {
    const { error } = await supabase.rpc('release_review_lock', { p_image_id: imageId });
    if (error) throw error;
  }

  static async validateImage(imageId: number): Promise<void> {
    const { error } = await supabase
      .from('project_images')
      .update({ is_validated: true, validated_at: new Date().toISOString() })
      .eq('id', imageId);
    if (error) throw error;
  }

  static async unvalidateImage(imageId: number): Promise<void> {
    const { error } = await supabase
      .from('project_images')
      .update({ is_validated: false, validated_at: null, validated_by: null })
      .eq('id', imageId);
    if (error) throw error;
  }

  static async invalidateImage(imageId: number, notes: string): Promise<void> {
    const { error } = await supabase.rpc('invalidate_image', {
      p_image_id: imageId, p_notes: notes ?? ''
    });
    if (error) throw error;
  }

  static async clearInvalidation(imageId: number): Promise<void> {
    const { error } = await supabase.rpc('clear_invalidation', { p_image_id: imageId });
    if (error) throw error;
  }

  static async markAnnotationWrong(imageId: number, lineId: number, notes?: string): Promise<void> {
    const projectId = await this.getProjectIdByImage(imageId);

    const { data, error: readErr } = await supabase
      .from('annotations')
      .select('id, data')
      .eq('project_id', projectId)
      .eq('image_id', imageId)
      .eq('id', lineId)
      .maybeSingle();

    if (readErr) throw readErr;
    if (!data) throw new Error('Annotation not found');

    const newData = {
      ...(data.data ?? {}),
      review_wrong: true,
      ...(notes ? { review_notes: notes } : {}),
    };

    const { error: writeErr } = await supabase
      .from('annotations')
      .update({ data: newData })
      .eq('project_id', projectId)
      .eq('image_id', imageId)
      .eq('id', lineId);

    if (writeErr) throw writeErr;
  }

  static async claimImageForReviewById(imageId: number) {
    const { data, error } = await supabase.rpc('claim_image_for_review_by_id', { p_image_id: imageId });
    if (error) throw error;
    // If another user has the lock it returns null
    return data as ImageInfo | null;
  }

  static async getPendingReviewCount(projectId: number): Promise<number> {
    const { count, error } = await supabase
      .from("project_images")
      .select("*", { count: "exact", head: true })
      .eq("project_id", projectId)
      .eq("is_invalidated", false)
      //.eq("has_lines", true)
      .or("has_lines.eq.true,is_non_trackable.eq.true")
      .or("is_validated.is.null,is_validated.eq.false");

    if (error) {
      console.error("Erro a contar pendentes:", error);
      return 0;
    }
    return count ?? 0;
  }

  static async clearAnnotationWrong(imageId: number, lineId: number): Promise<void> {
    const projectId = await this.getProjectIdByImage(imageId);

    const { data, error: readErr } = await supabase
      .from('annotations')
      .select('id, data')
      .eq('project_id', projectId)
      .eq('image_id', imageId)
      .eq('id', lineId)
      .maybeSingle();

    if (readErr) throw readErr;
    if (!data) throw new Error('Annotation not found');

    const newData = { ...(data.data ?? {}) };
    delete (newData as any).review_wrong;
    delete (newData as any).review_notes;

    const { error: writeErr } = await supabase
      .from('annotations')
      .update({ data: newData })
      .eq('project_id', projectId)
      .eq('image_id', imageId)
      .eq('id', lineId);

    if (writeErr) throw writeErr;
  }

  static async hasWrongAnnotations(imageId: number): Promise<boolean> {
    const projectId = await this.getProjectIdByImage(imageId);
    const { data, error } = await supabase
      .from('annotations')
      .select('data')
      .eq('project_id', projectId)
      .eq('image_id', imageId);

    if (error) {
      console.error('Erro a ler anotações:', error);
      return false;
    }
    return (data ?? []).some(r => (r as any).data?.review_wrong === true);
  }
}

// Gets image dimensions
async function getImageDimensions(file: File): Promise<{ width: number, height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = reject
      img.src = e.target?.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })  
}

export async function extractErr(e: any) {
  return e?.message || e?.error?.message || e?.data?.message || e?.hint || JSON.stringify(e);
}